import { useAppDispatch } from "./reduxHooks";
import { closeDocumentPreview, closeAllDocumentPreviews } from "../DocumentPreviewSlice_Store";
import { setLayoutMode } from "../LayoutSlice_Store";
import { closeDealDetail } from "../DealsTableSlice_Store";
import { closeExpertProfile } from "../ExpertsSlice_Store";
import { getDocTabName } from "./useWorkspaceTabs";
import type { DealSelection, ExpertSelection, WorkspaceResults } from "./useWorkspaceSelection";
import type { WorkspaceTabs } from "./useWorkspaceTabs";

export interface WorkspaceClosers {
  closeDocument: (docId: string) => void;
  closeDeal: () => void;
  closeExpert: () => void;
  closePanel: () => void;
}

interface CloseContext {
  deal: DealSelection;
  expert: ExpertSelection;
  results: WorkspaceResults;
  tabState: WorkspaceTabs;
}

/**
 * Pick the tab to show after a preview closes: the last remaining preview, or
 * failing that the first base tab. Leaves the active tab alone when the closed
 * document wasn't the one on screen.
 */
function tabAfterClose(ctx: CloseContext, docId: string): string | null {
  const { openDocuments } = ctx.results;
  const closedDoc = openDocuments.find((doc) => doc.docId === docId);
  if (!closedDoc || ctx.tabState.activeTab !== getDocTabName(closedDoc)) return null;

  const remainingDocs = openDocuments.filter((doc) => doc.docId !== docId);
  if (remainingDocs.length > 0) return getDocTabName(remainingDocs[remainingDocs.length - 1]);
  return ctx.tabState.baseTabs.length > 0 ? ctx.tabState.baseTabs[0] : null;
}

/** The three ways the workspace can be dismissed: a preview, a deal, or all of it. */
export function useWorkspaceClosers(
  deal: DealSelection,
  expert: ExpertSelection,
  results: WorkspaceResults,
  tabState: WorkspaceTabs
): WorkspaceClosers {
  const dispatch = useAppDispatch();
  const ctx: CloseContext = { deal, expert, results, tabState };

  const closeDocument = (docId: string): void => {
    dispatch(closeDocumentPreview(docId));

    const nextTab = tabAfterClose(ctx, docId);
    if (nextTab) tabState.setActiveTab(nextTab);

    // Collapse the workspace when that was the last thing keeping it open
    const remainingDocs = results.openDocuments.filter((doc) => doc.docId !== docId);
    const nothingLeftToShow = deal.inlineDealsFlag || !results.hasWorkspaceResults;
    if (
      remainingDocs.length === 0 &&
      !deal.hasDealSelected &&
      !expert.hasExpertSelected &&
      nothingLeftToShow
    ) {
      dispatch(setLayoutMode("chat"));
    }
  };

  /** Close the deal detail panel and collapse the RHS if no previews are open. */
  const closeDeal = (): void => {
    dispatch(closeDealDetail());
    if (results.openDocuments.length === 0) {
      dispatch(setLayoutMode("chat"));
    }
  };

  /** Close the expert profile panel and collapse the RHS if no previews are open. */
  const closeExpert = (): void => {
    dispatch(closeExpertProfile());
    if (results.openDocuments.length === 0) {
      dispatch(setLayoutMode("chat"));
    }
  };

  /** Global panel close — wipes open previews + deal detail + expert profile and re-centers chat. */
  const closePanel = (): void => {
    dispatch(closeAllDocumentPreviews());
    dispatch(closeDealDetail());
    dispatch(closeExpertProfile());
    dispatch(setLayoutMode("chat"));
  };

  return { closeDocument, closeDeal, closeExpert, closePanel };
}
